// Dialogue camera: one master image per scene, virtual shots that cut/push toward whoever speaks.
// Shots come from GET /stories/:id/dialogue-shots; rects are [x0, y0, x1, y1] fractions of the
// source image that already have the output aspect ratio.

import { FOCUS_WIDE } from './sceneScript';

export const DIALOGUE_CAMERA_MODES = [
  { id: 'speaker', label: 'Speaker close-ups', hint: 'The camera cuts to whoever is talking.' },
  { id: 'wide', label: 'Wide shot', hint: 'One shot of the whole scene.' },
  { id: 'animated', label: 'Animated speakers', hint: 'Lip-synced close-ups (uses video credits).' },
];

export const FULL_RECT = [0, 0, 1, 1];

export function dialogueCameraMode(story) {
  if (String(story?.story_type || '').toLowerCase() !== 'dialogue_story') return 'wide';
  const mode = String(story?.dialogue_camera || '').toLowerCase();
  return DIALOGUE_CAMERA_MODES.some((m) => m.id === mode) ? mode : 'speaker';
}

export function usesDialogueCamera(story) {
  return dialogueCameraMode(story) !== 'wide';
}

/** Copy of the scene script with one line's on-camera choice changed ('' = auto). */
export function setLineFocus(script, index, focus) {
  return (Array.isArray(script) ? script : []).map((line, i) => {
    if (i !== index || !line || typeof line !== 'object') return line;
    const { focus_character_id: _old, ...rest } = line;
    return focus ? { ...rest, focus_character_id: focus } : rest;
  });
}

/** On-camera choices for a line: Auto, each mapped character (left to right), Wide. */
export function lineFocusOptions(characterMap, characterById = {}) {
  const people = (characterMap?.characters || []).map((c) => ({
    value: c.character_id,
    label: characterById[c.character_id] || c.name || 'Character',
  }));
  return [{ value: '', label: 'Auto' }, ...people, { value: FOCUS_WIDE, label: 'Wide' }];
}

function smoothstep(p) {
  const x = Math.max(0, Math.min(1, p));
  return x * x * (3 - 2 * x);
}

/** Camera rect at shot-relative time t (same easing as the server renderer). */
export function rectAt(keyframes, t) {
  const frames = (keyframes || []).filter((k) => k && Array.isArray(k.rect) && k.rect.length === 4);
  if (!frames.length) return FULL_RECT;
  if (t <= Number(frames[0].t || 0)) return frames[0].rect;
  for (let i = 0; i < frames.length - 1; i += 1) {
    const a = frames[i];
    const b = frames[i + 1];
    const ta = Number(a.t || 0);
    const tb = Number(b.t || 0);
    if (t <= tb) {
      const p = tb > ta ? smoothstep((t - ta) / (tb - ta)) : 1;
      return a.rect.map((v, j) => v + (b.rect[j] - v) * p);
    }
  }
  return frames[frames.length - 1].rect;
}

/** CSS for an <img> inside an overflow-hidden box so only `rect` of the image is visible. */
export function cropImageStyle(rect) {
  const [x0, y0, x1, y1] = rect || FULL_RECT;
  const w = Math.max(0.01, x1 - x0);
  const h = Math.max(0.01, y1 - y0);
  return {
    position: 'absolute',
    width: `${100 / w}%`,
    height: `${100 / h}%`,
    left: `${(-x0 / w) * 100}%`,
    top: `${(-y0 / h) * 100}%`,
    maxWidth: 'none',
    objectFit: 'fill',
  };
}

/** Absolute box (percent of the image) for a character or camera rect overlay. */
export function boxStyle(box) {
  const [x0, y0, x1, y1] = box || FULL_RECT;
  return {
    position: 'absolute',
    left: `${x0 * 100}%`,
    top: `${y0 * 100}%`,
    width: `${(x1 - x0) * 100}%`,
    height: `${(y1 - y0) * 100}%`,
  };
}

/** Source pixel crop for a rect, nudged to the exact output aspect (mirrors the server). */
export function cropBoxPixels(rect, width, height, outAspect) {
  const [x0, y0, x1, y1] = rect || FULL_RECT;
  let cw = Math.max(1, (x1 - x0) * width);
  let ch = Math.max(1, (y1 - y0) * height);
  if (cw / ch > outAspect) cw = ch * outAspect;
  else ch = cw / outAspect;
  const cx = ((x0 + x1) / 2) * width;
  const cy = ((y0 + y1) / 2) * height;
  const sx = Math.min(Math.max(cx - cw / 2, 0), width - cw);
  const sy = Math.min(Math.max(cy - ch / 2, 0), height - ch);
  return { sx, sy, sw: cw, sh: ch };
}

/** Shot playing at scene-audio time t (the last shot holds past the end). */
export function shotAt(shots, t) {
  const list = shots || [];
  if (!list.length) return null;
  return list.find((s) => t < Number(s.end_seconds || 0)) || list[list.length - 1];
}

/** Camera rect for a scene clip at timeline time (clip-local time + trim = scene-audio time). */
export function sceneCameraRectAt(sceneShots, clipStartTime, trimStart, timelineTime) {
  const t = Math.max(0, Number(timelineTime || 0) - Number(clipStartTime || 0) + Number(trimStart || 0));
  const shot = shotAt(sceneShots?.shots, t);
  if (!shot) return null;
  return rectAt(shot.camera?.keyframes, t - Number(shot.start_seconds || 0));
}

/** Preview transform that shows only `rect` of an image laid out to fill the frame. */
export function focusPreviewStyle(rect) {
  if (!rect) return {};
  const [x0, y0, x1] = rect;
  const s = 1 / Math.max(0.01, x1 - x0);
  return {
    transformOrigin: '0 0',
    transform: `scale(${s.toFixed(4)}) translate(${(-x0 * 100).toFixed(3)}%, ${(-y0 * 100).toFixed(3)}%)`,
  };
}

/** Compact shot rows sent with an editor export clip (the server splits the clip into shots). */
export function dialogueShotsExportRows(sceneShots) {
  return (sceneShots?.shots || []).map((s) => ({
    start_seconds: Number(s.start_seconds || 0),
    end_seconds: Number(s.end_seconds || 0),
    keyframes: s.camera?.keyframes || [],
    video_url: s.video_url || null,
  }));
}

function shiftKeyframes(keyframes, offset, duration) {
  const frames = offset <= 0
    ? keyframes.map((k) => ({ ...k }))
    : [
      { t: 0, rect: rectAt(keyframes, offset) },
      ...keyframes
        .filter((k) => Number(k.t || 0) > offset)
        .map((k) => ({ ...k, t: Number((Number(k.t || 0) - offset).toFixed(3)) })),
    ];
  const last = frames[frames.length - 1];
  if (last && Number(last.t || 0) < duration) frames[frames.length - 1] = { ...last, t: Number(duration.toFixed(3)) };
  return frames;
}

/**
 * Export clip carrying `dialogue_shots` → one clip per shot inside its visible window
 * (same split as the server export, used by the on-device export).
 */
export function expandTimelineClip(clip) {
  const shots = (clip?.dialogue_shots || []).filter((s) => s && Array.isArray(s.keyframes) && s.keyframes.length);
  const { dialogue_shots: _rows, ...base } = clip || {};
  if (!clip || clip.video_url || !shots.length) return [base];
  const windowStart = Number(clip.trim_start || 0);
  const windowEnd = windowStart + Number(clip.playback_seconds || clip.duration_seconds || 0);
  const timelineStart = Number(clip.start_time || 0);
  const pieces = [];
  shots.forEach((shot, i) => {
    const shotStart = Number(shot.start_seconds || 0);
    const start = i === 0 ? windowStart : Math.max(windowStart, shotStart);
    const end = i === shots.length - 1 ? windowEnd : Math.min(windowEnd, Number(shot.end_seconds || 0));
    if (end - start < 0.05) return;
    const duration = Number((end - start).toFixed(3));
    const offset = Math.max(0, start - shotStart);
    const lipVideo = String(shot.video_url || '').trim() || null;
    pieces.push({
      ...base,
      video_url: lipVideo,
      start_time: Number((timelineStart + (start - windowStart)).toFixed(3)),
      trim_start: lipVideo ? Number(offset.toFixed(3)) : 0,
      trim_end: lipVideo ? Number((offset + duration).toFixed(3)) : duration,
      duration_seconds: duration,
      playback_seconds: duration,
      timeline_seconds: duration,
      video_playback_speed: 1,
      video_media_duration_seconds: undefined,
      motion_preset: 'none',
      audio_motion_timeline: null,
      focus_camera: { keyframes: shiftKeyframes(shot.keyframes, offset, duration) },
      transition_out: 'cut',
      transition_duration: 0,
    });
  });
  if (!pieces.length) return [base];
  pieces[pieces.length - 1].transition_out = clip.transition_out || 'cut';
  pieces[pieces.length - 1].transition_duration = clip.transition_duration || 0;
  return pieces;
}

export function shotsBySceneId(payload) {
  const out = {};
  for (const row of payload?.scenes || []) {
    if (row?.scene_id && Array.isArray(row.shots) && row.shots.length) out[row.scene_id] = row;
  }
  return out;
}
